import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

import { BudgetRedirectCardPayload } from '../../../../services/gable/gable.service';
import { GableDrawerService } from '../../../../services/gable/gable-drawer.service';

/**
 * Inline link card for the `redirect_to_budget` tool result. Renders a
 * blue underlined "Go to budgets now" link routed to either the drafts
 * page (build/edit intent) or the live budget detail page (view-only
 * intent), based on the backend's `is_draft` discriminator.
 *
 * Visual + interaction model match the report card's action row — same
 * inline blue underline affordance used elsewhere in the v2 layout for
 * "Download attachment" / "View detail" links.
 */
@Component({
    selector: 'app-gable-budget-redirect-card',
    templateUrl: './budget-redirect-card.component.html',
    styleUrls: ['./budget-redirect-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        RouterModule,
    ],
})
export class GableBudgetRedirectCardComponent {
    @Input({ required: true }) payload!: BudgetRedirectCardPayload;
    @Input() tool: string = 'redirect_to_budget';

    /** open_page keeps the chat open beside the page; Budget AI takes over, so that one closes. */
    get staysInDrawer(): boolean {
        return this.tool === 'open_page';
    }

    constructor(private _drawer: GableDrawerService) {}

    /**
     * Path-only portion of the redirect target. Backend can send
     * `"/app/budgets/detail?gable_intent=…&gable_conversation=…"` (single
     * string); we split here so RouterLink gets a real path and the
     * query params go through `[queryParams]` — otherwise Angular treats
     * the whole `?…` blob as a literal path segment and the user lands
     * on a 404.
     */
    get urlPath(): string {
        const raw = this.payload?.url ?? '/app/budgets/detail';
        const qIndex = raw.indexOf('?');
        return qIndex >= 0 ? raw.slice(0, qIndex) : raw;
    }

    get urlQueryParams(): Record<string, string> {
        const raw = this.payload?.url ?? '';
        const qIndex = raw.indexOf('?');
        if (qIndex < 0) {
            return {};
        }
        const params: Record<string, string> = {};
        const query = raw.slice(qIndex + 1);
        for (const pair of query.split('&')) {
            if (pair === '') {
                continue;
            }
            const eq = pair.indexOf('=');
            if (eq < 0) {
                params[decodeURIComponent(pair)] = '';
                continue;
            }
            const k = decodeURIComponent(pair.slice(0, eq));
            const v = decodeURIComponent(pair.slice(eq + 1));
            params[k] = v;
        }
        return params;
    }

    get label(): string {
        return this.payload?.label ?? 'Go to budgets now';
    }

    get helperText(): string {
        return this.payload?.helper_text ?? '';
    }

    /**
     * The Budget Drafts / Budget Detail pages have their own AI drawer
     * (Budget AI) that conflicts visually with Gable. Closing Gable on
     * click hands the user a clean target — Budget AI takes over once
     * the destination page mounts. We only do this for the budget
     * redirect card; other navigation (report card, deep links inside
     * answers) leaves Gable open so the user can keep asking questions.
     */
    onNavigate(): void {
        this._drawer.close();
    }
}
